import type { ManagedAgentId, ProjectId } from "@taskin-code/contracts";
import { useState } from "react";

import { useHasProjectPermission } from "../../auth/authStore";
import { useVisibleManagedAgents } from "../../hooks/useProviderModelCatalog";
import { newCommandId } from "../../lib/utils";
import { readNativeApi } from "../../nativeApi";
import { useStore } from "../../store";
import { AgentAvatar } from "../AgentAvatar";
import { Switch } from "../ui/switch";
import {
  filterCoordinators,
  projectAgentWarning,
  sortProjectAgents,
} from "./ProjectAgentsSection.logic";

export function ProjectAgentsSection(props: { readonly projectId: ProjectId }) {
  const project = useStore((state) =>
    state.projects.find((candidate) => candidate.id === props.projectId),
  );
  const agents = useVisibleManagedAgents();
  const canEdit = useHasProjectPermission(props.projectId, "p.edit");
  const [pendingId, setPendingId] = useState<ManagedAgentId | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (project === undefined) return null;

  const selected = new Set(project.agentIds);
  const sortedAgents = sortProjectAgents(filterCoordinators(agents));

  const toggle = async (agentId: ManagedAgentId, checked: boolean) => {
    const nextAgentIds = checked
      ? [...project.agentIds, agentId]
      : project.agentIds.filter((id) => id !== agentId);
    const api = readNativeApi();
    if (!api) {
      setError("No hay conexión con el servidor.");
      return;
    }
    setPendingId(agentId);
    setError(null);
    try {
      await api.orchestration.dispatchCommand({
        type: "project.meta.update",
        commandId: newCommandId(),
        projectId: project.id,
        agentIds: nextAgentIds,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar el agente.");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        Elige los agentes gestionados que pueden iniciar iniciativas en este proyecto.
      </p>
      <div className="rounded-md border border-border/60 bg-card/40 p-4">
        {sortedAgents.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No hay coordinadores visibles. Crea un agente con modo &quot;Coordinator&quot; en
            Ajustes → Agentes.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {sortedAgents.map((agent) => {
              const warning = projectAgentWarning(agent);
              return (
                <label
                  key={agent.id}
                  className="flex items-center justify-between gap-3 rounded border border-border/40 px-3 py-2"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <AgentAvatar
                      agentId={agent.id}
                      displayName={agent.name}
                      avatarFileName={agent.avatarFileName}
                      size="sm"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{agent.name}</span>
                      {agent.description ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {agent.description}
                        </span>
                      ) : null}
                      {warning ? (
                        <span className="block text-[11px] text-muted-foreground">{warning}</span>
                      ) : null}
                    </span>
                  </span>
                  <Switch
                    checked={selected.has(agent.id)}
                    disabled={!canEdit || pendingId !== null}
                    onCheckedChange={(checked) => void toggle(agent.id, checked)}
                    aria-label={`Permitir que ${agent.name} inicie iniciativas en ${project.name}`}
                  />
                </label>
              );
            })}
          </div>
        )}
        {!canEdit ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Necesitas el permiso <code>p.edit</code> en este proyecto para cambiar sus agentes.
          </p>
        ) : null}
        {error ? (
          <div className="mt-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
